import { useMemo, useState } from 'react'
import { FieldLabel } from '../ui/field-label'
import { Input } from '../ui/input'
import { SelectPicker } from '../ui/select-picker'
import { Button } from '../ui/button'
import {
  CASE_FILE_TYPE_LABELS,
  CASE_FILE_TYPES,
  type CaseFileType,
} from '../../types/case'

export type PendingCaseMedia = {
  id: string
  file: File
  type: CaseFileType
}

type CaseMediaUploaderProps = {
  /** Controlled pending files (create flow). When omitted, component is uncontrolled. */
  files?: PendingCaseMedia[]
  onChange?: (files: PendingCaseMedia[]) => void
  disabled?: boolean
  /** When provided, shows Upload button that posts via parent handler. */
  onUpload?: (files: PendingCaseMedia[]) => Promise<void> | void
  uploading?: boolean
  title?: string
  /** Defaults to pre_op / post_op. Pass `['outcome']` on the Outcome tab. */
  allowedTypes?: CaseFileType[]
}

function createPending(file: File, type: CaseFileType): PendingCaseMedia {
  return {
    id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
    file,
    type,
  }
}

/**
 * Multi-file picker with per-file type (legacy parity).
 * Used on case create (deferred upload), case detail Files tab, and Outcome tab.
 */
export function CaseMediaUploader({
  files: controlledFiles,
  onChange,
  disabled = false,
  onUpload,
  uploading = false,
  title = 'Case images / files',
  allowedTypes = CASE_FILE_TYPES,
}: CaseMediaUploaderProps) {
  const [internalFiles, setInternalFiles] = useState<PendingCaseMedia[]>([])
  const files = controlledFiles ?? internalFiles
  const defaultType = allowedTypes[0] ?? 'pre_op'

  const setFiles = (next: PendingCaseMedia[]) => {
    if (onChange) {
      onChange(next)
      return
    }
    setInternalFiles(next)
  }

  const typeOptions = useMemo(
    () =>
      allowedTypes.map((type) => ({
        value: type,
        label: CASE_FILE_TYPE_LABELS[type],
      })),
    [allowedTypes],
  )

  const handlePick = (list: FileList | null) => {
    if (!list || list.length === 0) {
      return
    }

    const next = [...files]
    Array.from(list).forEach((file) => {
      next.push(createPending(file, defaultType))
    })
    setFiles(next)
  }

  const updateType = (id: string, type: string) => {
    setFiles(
      files.map((item) =>
        item.id === id ? { ...item, type: type as CaseFileType } : item,
      ),
    )
  }

  const remove = (id: string) => {
    setFiles(files.filter((item) => item.id !== id))
  }

  return (
    <div className="space-y-3 rounded-md border border-parch-100 p-4">
      <div>
        <h3 className="text-sm font-semibold text-ink-800">{title}</h3>
        <p className="mt-1 text-xs text-ink-500">
          Select multiple images or PDFs
          {allowedTypes.length > 1
            ? '. Set a type for each file.'
            : ` as ${CASE_FILE_TYPE_LABELS[defaultType]}.`}
        </p>
      </div>

      <div>
        <FieldLabel optional>Add files</FieldLabel>
        <Input
          type="file"
          multiple
          accept="image/jpeg,image/png,image/jpg,application/pdf"
          disabled={disabled || uploading}
          onChange={(event) => {
            handlePick(event.target.files)
            event.target.value = ''
          }}
        />
      </div>

      {files.length > 0 && (
        <ul className="space-y-2">
          {files.map((item) => (
            <li
              key={item.id}
              className="flex flex-wrap items-center gap-3 rounded-md border border-parch-100 bg-parch-50 px-3 py-2"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-ink-800">{item.file.name}</p>
                <p className="text-xs text-ink-500">{Math.round(item.file.size / 1024)} KB</p>
              </div>
              <div className="w-36">
                <SelectPicker
                  options={typeOptions}
                  value={item.type}
                  onChange={(value) => updateType(item.id, value)}
                />
              </div>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={disabled || uploading}
                onClick={() => remove(item.id)}
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}

      {onUpload && (
        <Button
          type="button"
          size="sm"
          disabled={disabled || uploading || files.length === 0}
          onClick={() => void onUpload(files)}
        >
          {uploading
            ? 'Uploading…'
            : `Upload ${files.length || ''} File${files.length === 1 ? '' : 's'}`.trim()}
        </Button>
      )}
    </div>
  )
}

export function buildCaseFilesFormData(files: PendingCaseMedia[]): FormData {
  const formData = new FormData()
  files.forEach((item, index) => {
    formData.append(`files[${index}]`, item.file)
    formData.append(`types[${index}]`, item.type)
  })
  return formData
}
